import React, { useState } from 'react';
import { Button, Popover, Checkbox } from 'antd';
import { SettingOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ColumnInfo {
  key: string;
  title: string;
  fixed?: boolean | string;
}

interface ColumnSelectorProps {
  availableColumns: ColumnInfo[];
  visibleColumns: string[];
  onColumnChange: (columns: string[]) => void;
  fixedColumns?: string[];
  buttonLabel?: string;
}

// 可拖拽的列项组件
interface SortableColumnItemProps {
  id: string;
  title: string;
  checked: boolean;
  disabled: boolean;
  onCheckChange: (checked: boolean) => void;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  id,
  title,
  checked,
  disabled,
  onCheckChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    marginBottom: 4,
    borderRadius: 4,
    background: isDragging ? '#e6f7ff' : '#fafafa',
    border: isDragging ? '1px dashed #1890ff' : '1px solid #f0f0f0',
    cursor: disabled ? 'not-allowed' : 'default',
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* 拖拽手柄 */}
      <span
        {...listeners}
        style={{
          cursor: disabled ? 'not-allowed' : 'grab',
          marginRight: 8,
          color: disabled ? '#ccc' : '#999',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <HolderOutlined />
      </span>
      {/* 复选框 */}
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckChange(e.target.checked)}
        style={{ flex: 1 }}
      >
        {title}
      </Checkbox>
    </div>
  );
};

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  availableColumns,
  visibleColumns,
  onColumnChange,
  fixedColumns = ['actions'],
  buttonLabel = '列设置'
}) => {
  const [open, setOpen] = useState(false);

  // 构建当前排序列表：已选中的按 visibleColumns 顺序，未选中的按 availableColumns 原始顺序追加
  const buildOrderedColumns = (): ColumnInfo[] => {
    const visibleSet = new Set(visibleColumns);
    const fixedSet = new Set(fixedColumns);
    
    // 先按 visibleColumns 的顺序添加已选中的列（排除固定列）
    const ordered: ColumnInfo[] = [];
    const seen = new Set<string>();
    
    visibleColumns.forEach(key => {
      if (fixedSet.has(key)) return;
      const col = availableColumns.find(c => c.key === key);
      if (col && !seen.has(key)) {
        ordered.push(col);
        seen.add(key);
      }
    });
    
    // 再追加未选中的列
    availableColumns.forEach(col => {
      if (fixedSet.has(col.key)) return;
      if (!seen.has(col.key)) {
        ordered.push(col);
        seen.add(col.key);
      }
    });
    
    return ordered;
  };

  const [orderedColumns, setOrderedColumns] = useState<ColumnInfo[]>(buildOrderedColumns);

  // Popover 打开时重新计算排序
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setOrderedColumns(buildOrderedColumns());
    }
    setOpen(newOpen);
  };

  // 拖拽传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 处理拖拽结束
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderedColumns.findIndex(col => col.key === active.id);
      const newIndex = orderedColumns.findIndex(col => col.key === over.id);
      const newOrder = arrayMove(orderedColumns, oldIndex, newIndex);
      setOrderedColumns(newOrder);
      
      // 立即更新选中的列顺序
      const visibleSet = new Set(visibleColumns);
      const newVisibleOrder = newOrder
        .filter(col => visibleSet.has(col.key))
        .map(col => col.key);
      
      // 添加固定列
      fixedColumns.forEach(fixedCol => {
        if (!newVisibleOrder.includes(fixedCol)) {
          if (fixedCol === 'actions') {
            newVisibleOrder.push(fixedCol);
          } else {
            newVisibleOrder.unshift(fixedCol);
          }
        }
      });
      
      onColumnChange(newVisibleOrder);
    }
  };

  // 处理勾选变化
  const handleCheckChange = (columnKey: string, checked: boolean) => {
    const visibleSet = new Set(visibleColumns);
    
    if (checked) {
      visibleSet.add(columnKey);
    } else {
      visibleSet.delete(columnKey);
    }
    
    // 按当前排序顺序重建 visibleColumns
    const newVisibleOrder = orderedColumns
      .filter(col => visibleSet.has(col.key))
      .map(col => col.key);
    
    // 添加固定列
    fixedColumns.forEach(fixedCol => {
      if (!newVisibleOrder.includes(fixedCol)) {
        if (fixedCol === 'actions') {
          newVisibleOrder.push(fixedCol);
        } else {
          newVisibleOrder.unshift(fixedCol);
        }
      }
    });
    
    onColumnChange(newVisibleOrder);
  };

  const visibleSet = new Set(visibleColumns);
  const fixedSet = new Set(fixedColumns);

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      content={
        <div style={{ width: 280, maxHeight: 450, overflow: 'auto' }}>
          <div style={{ marginBottom: 12, fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>列显示与排序</span>
            <span style={{ fontSize: 12, color: '#999', fontWeight: 'normal' }}>拖拽调整顺序</span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedColumns.map(col => col.key)}
              strategy={verticalListSortingStrategy}
            >
              {orderedColumns.map(column => (
                <SortableColumnItem
                  key={column.key}
                  id={column.key}
                  title={column.title}
                  checked={visibleSet.has(column.key)}
                  disabled={fixedSet.has(column.key)}
                  onCheckChange={(checked) => handleCheckChange(column.key, checked)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      }
      title="选择并排序显示列"
      trigger="click"
      placement="bottomRight"
    >
      <Button icon={<SettingOutlined />} title={buttonLabel}>
        {buttonLabel}
      </Button>
    </Popover>
  );
};

export default ColumnSelector;
